import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Percent,
  Building2,
  Smartphone,
  StickyNote,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EditUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    nif: '',
    iban: '',
    bank_name: '',
    revolut_account: '',
    profit_percentage: 8.6,
    admin_notes: ''
  });
  const [originalEmail, setOriginalEmail] = useState('');

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}`);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        nif: response.data.nif || '',
        iban: response.data.iban || '',
        bank_name: response.data.bank_name || '',
        revolut_account: response.data.revolut_account || '',
        profit_percentage: response.data.profit_percentage || 8.6,
        admin_notes: response.data.admin_notes || ''
      });
      setOriginalEmail(response.data.email || '');
    } catch (error) {
      // Error silenced: 'Failed to fetch user:', error);
      toast.error('Utilizador não encontrado');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/admin/users/${userId}`, {
        ...formData,
        profit_percentage: parseFloat(formData.profit_percentage)
      });
      toast.success('Utilizador atualizado com sucesso');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar utilizador');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="edit-user-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/users')}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Editar Utilizador</h1>
          <p className="text-slate-400 text-sm">{formData.name}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <User className="w-5 h-5 text-blue-400" />
            Informações Básicas
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300">Nome completo</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                required
                data-testid="edit-name"
              />
            </div>

            <div className="md:col-span-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email (login)
                {formData.email !== originalEmail && (
                  <span className="text-amber-400 text-xs ml-2">⚠ O utilizador fará login com este novo email</span>
                )}
              </Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                required
                data-testid="edit-email"
              />
            </div>

            <div>
              <Label className="text-slate-300">Telefone</Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+351 912 345 678"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-phone"
              />
            </div>

            <div>
              <Label className="text-slate-300 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                NIF (Nº Contribuinte)
              </Label>
              <Input
                name="nif"
                value={formData.nif}
                onChange={handleChange}
                placeholder="123456789"
                maxLength={9}
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl font-mono"
                data-testid="edit-nif"
              />
            </div>

            <div>
              <Label className="text-slate-300 flex items-center gap-2">
                <Percent className="w-4 h-4" />
                Taxa de Rendimento (%)
              </Label>
              <Input
                name="profit_percentage"
                type="number"
                step="0.1"
                value={formData.profit_percentage}
                onChange={handleChange}
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                required
                data-testid="edit-percentage"
              />
            </div>

            <div className="md:col-span-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Morada
              </Label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Morada completa"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-address"
              />
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-400" />
            Dados Bancários
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Nome do Banco</Label>
              <Input
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                placeholder="Ex: Millennium BCP"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-bank"
              />
            </div>

            <div>
              <Label className="text-slate-300 flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                Conta Revolut
              </Label>
              <Input
                name="revolut_account"
                value={formData.revolut_account}
                onChange={handleChange}
                placeholder="@username"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-revolut"
              />
            </div>

            <div className="md:col-span-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                IBAN
              </Label>
              <Input
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="PT50 0000 0000 0000 0000 0000 0"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl font-mono"
                data-testid="edit-iban"
              />
            </div>
          </div>
        </div>

        {/* Admin Notes */}
        <div className="glass-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-amber-400" />
            Notas do Administrador
            <span className="text-xs text-slate-500 font-normal">(apenas visível para admin)</span>
          </h2>

          <Textarea
            name="admin_notes"
            value={formData.admin_notes}
            onChange={handleChange}
            placeholder="Notas privadas sobre este cliente... Ex: Cliente VIP, prefere contacto por WhatsApp, etc."
            className="min-h-32 bg-[#161C22] border-white/10 text-white rounded-xl"
            data-testid="edit-notes"
          />
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold"
          data-testid="save-user"
        >
          {saving ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              A guardar...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Guardar Alterações
            </div>
          )}
        </Button>
      </form>
    </div>
  );
}
